import { Modal } from '@/components/common/dialog';
import { ModalButton } from '@/components/common/modal-button';
import { ModalTransportationButton } from '@/components/common/modal-transportation-button';
import { LocationMarkerIcon, MarkerIcon, RouteIcon } from '@/components/icons';
import ShopPhoneIcon from '@/components/icons/shop/Phone';
import PlaceHolder from '@/components/icons/shop/PlaceHolder';
import { useOrientation } from '@/hooks';
import useBaseSize from '@/hooks/useBaseSize';
import type { BuildingInStoreInfo } from '@/services/dto';
import { useConfigStore } from '@/stores';
import type { PoiContentSlide } from '@/types/shop';
import { AppUtils } from '@/utils/app';
import { getPoiBuildingName } from '@/utils/duty-free';
import poiUtils from '@/utils/poi';
import { getHSize, getSize } from '@/utils/size';
import clsx from 'clsx';
import { useEffect, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import styles from './styles.module.scss';

interface ShopPoiModalProps {
    isOpen: boolean;
    language: string;
    poiContent: PoiContentSlide | null;
    buildings: BuildingInStoreInfo[];
    screenMode: 'split' | 'fullscreen';
    onClose: () => void;
    onGuideStart: () => void;
    onLocationCheck: () => void;
}

const ShopPoiModal = ({ isOpen, language, poiContent, buildings, screenMode, onClose, onGuideStart, onLocationCheck }: ShopPoiModalProps) => {
    const { isHorizontal } = useOrientation();
    const { t } = useTranslation();
    const { baseWidth, baseHeight } = useBaseSize();
    const currentLanguage = useConfigStore((state) => state.config.ui.language);
    const contrast = useConfigStore((state) => state.config.ui.contrast);
    const prevActiveElementRef = useRef<HTMLElement | null>(null);

    useEffect(() => {
        if (isOpen && !prevActiveElementRef.current) {
            prevActiveElementRef.current = document.activeElement as HTMLElement;
        }
    }, [isOpen]);

    const poiContentFormat = useMemo(() => {
        const dataFormat = {
            imgUrl: poiContent?.data.contents.shopTmrkImgUrlApp?.[language]
                ? poiContent?.data.contents.shopTmrkImgUrlApp?.[language]
                : poiContent?.data.contents.shopTmrkImgUrlApp?.['ko'],
            categoryName: poiContent?.category?.name?.[language] ? poiContent?.category?.name?.[language] : poiContent?.category?.name?.['ko'],
            shopCntc: poiContent?.data.contents.shopCntc?.[language]
                ? poiContent?.data.contents.shopCntc?.[language]
                : poiContent?.data.contents.shopCntc?.['ko'],
        };
        return {
            imgUrl: dataFormat.imgUrl ?? '',
            categoryName: dataFormat.categoryName ?? '',
            shopCntc: dataFormat.shopCntc ?? '',
        };
    }, [poiContent]);

    if (!poiContent) return null;

    const defaultBuildingName = poiContent.data?.contents?.cstrTownNm?.[language] || poiContent.data?.contents?.cstrTownNm?.['ko'] || '';
    const buildingName = getPoiBuildingName(poiContent.data, language, defaultBuildingName);

    return (
        <Modal
            isOpen={isOpen}
            onClose={onClose}
            className={clsx(styles['poi-modal'], {
                [styles['poi-modal__vertical']]: !isHorizontal,
                [styles['poi-modal__horizontal']]: isHorizontal,
                [styles['poi-modal__vertical--halfmode']]: screenMode === 'split' && !isHorizontal,
            })}
            scrollBarClassName="max-h-full! overflow-y-hidden!"
            wrapperClassName="items-center! pb-0!"
        >
            <div className={styles['poi-modal__container']}>
                <div
                    className={clsx(styles['poi-modal__container--header'], {
                        [styles['poi-modal__container--header--vertical']]: !isHorizontal,
                    })}
                >
                    <h1 data-interactive data-modal-region tabIndex={0} aria-label={poiContent.name} className="focus-border">
                        {poiContent.name}
                    </h1>
                </div>
                <div
                    className={clsx(styles['poi-modal__container--logo'], 'focus-border', {
                        [styles['poi-modal__container--logo--vertical']]: !isHorizontal,
                        [styles['poi-modal__container--logo--contrast']]: contrast,
                    })}
                    tabIndex={0}
                    data-interactive
                    aria-label={t('shop.modal.tts.logo_desc', { poiName: poiContent.name })}
                >
                    {poiContentFormat.imgUrl ? (
                        <img src={poiContentFormat.imgUrl} alt={poiContent.name} className={styles.img_contain} />
                    ) : (
                        <div className={styles['poi-modal__container--logo--empty']}>
                            <PlaceHolder />
                        </div>
                    )}
                </div>
                <div
                    className={clsx({
                        [styles['poi-modal__container--content']]: currentLanguage === 'ko',
                        [`widescreen:h-[calc(132*var(--vh-unit))] h-[calc(156*var(--vh-unit))]`]: currentLanguage !== 'ko',
                    })}
                >
                    <div
                        className={clsx({
                            [styles['poi-modal__container--content--wrapper']]: currentLanguage === 'ko',
                            [`flex h-full flex-col items-start justify-center gap-[calc(12*var(--vh-unit))] px-[calc(56*var(--vw-unit))]`]:
                                currentLanguage !== 'ko',
                        })}
                    >
                        <div
                            className={clsx(styles['poi-modal__container--content--location'], 'focus-border', {
                                ['w-[95%]']: currentLanguage !== 'ko',
                            })}
                            tabIndex={0}
                            data-interactive
                            aria-label={`${t('common.tts.location')} ${buildings.length > 1 ? buildingName : ''}, ${t('shop.search.tts.content.floor', { floor: AppUtils.getFloorTTSText(poiContent.floor) })}, ${poiContentFormat.categoryName}`}
                        >
                            <div className="h-[calc(36*var(--vh-unit))] w-[calc(36*var(--vw-unit))]">
                                <LocationMarkerIcon width={36} height={36} />
                            </div>
                            <div
                                className={clsx(styles['poi-modal__container--content--location--info'], {
                                    [styles['poi-modal__container--content--location--info--vertical']]: !isHorizontal,
                                    ['w-full!']: currentLanguage !== 'ko',
                                })}
                            >
                                {buildings.length >= 1 && (
                                    <>
                                        <span className={styles['poi-modal__container--content--location--info--building-name']}>{buildingName}</span>
                                        <div className={styles['poi-modal__container--content--location--info--divider']}></div>
                                    </>
                                )}
                                <span className={clsx('number', styles['poi-modal__container--content--location--info--floor'])}>{poiContent.floor}</span>
                                <span className={styles['poi-modal__container--content--location--info--category']}>{poiContentFormat.categoryName}</span>
                            </div>
                        </div>
                        {poiContentFormat.shopCntc && (
                            <div
                                className={clsx(
                                    `number ${currentLanguage !== 'ko' ? 'w-full' : 'w-[calc(250*var(--vw-unit))]'}`,
                                    styles['poi-modal__container--content--phone'],
                                    'focus-border',
                                    currentLanguage === 'ko' ? 'justify-end' : ''
                                )}
                                tabIndex={0}
                                data-interactive
                                aria-label={`${t('common.tts.phone_number')} ${AppUtils.getPhoneNumberTTSText(poiContentFormat.shopCntc)}`}
                            >
                                <ShopPhoneIcon width={getSize(36, baseWidth)} height={getHSize(36, baseHeight)} />
                                <span>{poiContentFormat.shopCntc}</span>
                            </div>
                        )}
                    </div>
                </div>

                {isHorizontal ? (
                    <div
                        className={clsx(
                            styles['poi-modal__container--footer'],
                            'widescreen:flex hidden items-center justify-center gap-[calc(23*var(--vw-unit))]'
                        )}
                    >
                        <ModalTransportationButton
                            variant="primary"
                            type="button"
                            role="button"
                            tabIndex={0}
                            data-interactive
                            aria-label={t('shop.modal.text.buttons.location_check')}
                            icon={<MarkerIcon width={80} height={80} />}
                            onClick={onLocationCheck}
                            data-role={t('common.data_role.button')}
                        >
                            {t('shop.modal.text.buttons.location_check')}
                        </ModalTransportationButton>

                        <ModalTransportationButton
                            variant="secondary"
                            type="button"
                            role="button"
                            tabIndex={0}
                            data-interactive
                            aria-label={t('shop.modal.text.buttons.direction_guide')}
                            icon={<RouteIcon width={80} height={80} />}
                            onClick={onGuideStart}
                            data-role={t('common.data_role.button')}
                        >
                            {t('shop.modal.text.buttons.direction_guide')}
                        </ModalTransportationButton>
                    </div>
                ) : (
                    <div className={clsx(styles['poi-modal__container--footer'], 'widescreen:hidden flex items-center justify-center gap-[23px]')}>
                        <ModalButton
                            variant="primary"
                            type="button"
                            role="button"
                            size="lg"
                            tabIndex={0}
                            data-interactive
                            aria-label={t('shop.modal.text.buttons.location_check')}
                            icon={<MarkerIcon width={80} height={80} />}
                            onClick={onLocationCheck}
                            data-role={t('common.data_role.button')}
                        >
                            {t('shop.modal.text.buttons.location_check')}
                        </ModalButton>

                        <ModalButton
                            variant="secondary"
                            type="button"
                            role="button"
                            size="lg"
                            tabIndex={0}
                            data-interactive
                            aria-label={t('shop.modal.text.buttons.direction_guide')}
                            icon={<RouteIcon width={80} height={80} />}
                            onClick={onGuideStart}
                            data-role={t('common.data_role.button')}
                        >
                            {t('shop.modal.text.buttons.direction_guide')}
                        </ModalButton>
                    </div>
                )}
            </div>
        </Modal>
    );
};

export default ShopPoiModal;
